import referentsJson from "../../../data/blank-referents.json";
import {
  isRecord,
  validationFailure,
  validationSuccess,
  type ValidationResult,
} from "../../../data/validation";

/**
 * Which of the two doctrinal lists a term belongs to. Ruling 29,
 * 18 September 2026.
 *
 * `A` is the 15 control measures of EO 103.05. `B` is everything else a blank
 * can point at — a point on a route, a time, a frequency, a callsign. The
 * split exists because the 22 terms were doing two jobs and the 15 answer only
 * one of them.
 */
export type ReferentList = "A" | "B";

/**
 * What shape of thing the term names. Ruling 32, 18 September 2026.
 *
 * A `linear` measure is a line — a report line, a boundary, an axis. It has no
 * single grid, and a grid on one is refused.
 *
 * A `located` measure is a point or an area and carries one.
 *
 * `null` is **unstated**, not unknown. Ruling 32 named the measures it named;
 * the rest carry no geometry because classifying them would be an
 * implementer's judgement, and nothing refuses or expects a grid on them.
 */
export type ReferentGeometry = "linear" | "located";

export interface BlankReferent {
  readonly geometry: ReferentGeometry | null;
  readonly id: string;
  readonly list: ReferentList;
  readonly patterns: readonly string[];
  /**
   * The doctrinal source for this term, or `null` for **UNGROUNDED**.
   *
   * An ungrounded term still types blanks. It is flagged, not disabled: the
   * flag says nobody has yet pointed at the doctrine behind it, and grounding
   * one is Captain Holloway's to rule, never an implementer's to supply.
   */
  readonly source: string | null;
}

export interface BlankReferentVocabulary {
  readonly note: string;
  readonly referents: readonly BlankReferent[];
  readonly schemaVersion: 3;
  readonly source: string;
}

function nonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

export function validateBlankReferents(
  value: unknown,
  source: string,
): ValidationResult<BlankReferentVocabulary> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  if (value.schemaVersion !== 3) {
    return validationFailure(
      source,
      "$.schemaVersion",
      "expected schemaVersion 3",
    );
  }
  for (const key of ["source", "note"] as const) {
    if (!nonEmpty(value[key])) {
      return validationFailure(
        source,
        `$.${key}`,
        "expected a non-empty string",
      );
    }
  }
  if (!Array.isArray(value.referents) || value.referents.length === 0) {
    return validationFailure(
      source,
      "$.referents",
      "expected a non-empty array",
    );
  }
  const seen = new Set<string>();
  for (const [index, entry] of value.referents.entries()) {
    const path = `$.referents[${String(index)}]`;
    if (!isRecord(entry)) {
      return validationFailure(source, path, "expected a JSON object");
    }
    if (!nonEmpty(entry.id)) {
      return validationFailure(
        source,
        `${path}.id`,
        "expected a non-empty string",
      );
    }
    if (seen.has(entry.id)) {
      return validationFailure(source, `${path}.id`, "duplicate referent id");
    }
    seen.add(entry.id);
    if (
      !Array.isArray(entry.patterns) ||
      entry.patterns.length === 0 ||
      !entry.patterns.every(nonEmpty)
    ) {
      return validationFailure(
        source,
        `${path}.patterns`,
        "expected a non-empty array of non-empty strings",
      );
    }
    if (entry.list !== "A" && entry.list !== "B") {
      return validationFailure(source, `${path}.list`, 'expected "A" or "B"');
    }
    if (entry.source !== null && !nonEmpty(entry.source)) {
      return validationFailure(
        source,
        `${path}.source`,
        "expected a doctrinal source, or null for UNGROUNDED",
      );
    }
    if (entry.list === "A" && entry.source === null) {
      return validationFailure(
        source,
        `${path}.source`,
        "a control measure is grounded in EO 103.05 and cannot be UNGROUNDED",
      );
    }
    if (
      entry.geometry !== null &&
      entry.geometry !== "linear" &&
      entry.geometry !== "located"
    ) {
      return validationFailure(
        source,
        `${path}.geometry`,
        'expected "linear", "located", or null for unstated',
      );
    }
  }
  return validationSuccess(value as unknown as BlankReferentVocabulary);
}

const validation = validateBlankReferents(
  referentsJson,
  "app/data/blank-referents.json",
);

/* v8 ignore next -- the imported, committed JSON is validated in unit tests */
if (!validation.ok) throw new Error(validation.error.message);

export const blankReferents: BlankReferentVocabulary = Object.freeze(
  validation.value,
);

function escapeLiteral(pattern: string): string {
  return pattern.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

export interface ReferentPattern {
  readonly id: string;
  readonly matcher: RegExp;
}

/**
 * One case-insensitive, word-bounded matcher per pattern. Patterns are literal
 * text from the vocabulary file and are escaped, so the file cannot inject a
 * regular expression.
 */
export function referentPatterns(
  vocabulary: BlankReferentVocabulary = blankReferents,
): readonly ReferentPattern[] {
  return vocabulary.referents.flatMap((referent) =>
    referent.patterns.map((pattern) => ({
      id: referent.id,
      matcher: new RegExp(`\\b${escapeLiteral(pattern)}\\b`, "gi"),
    })),
  );
}

/** List A. The control measures of EO 103.05. Ruling 29. */
export function controlMeasures(
  vocabulary: BlankReferentVocabulary = blankReferents,
): readonly BlankReferent[] {
  return vocabulary.referents.filter((referent) => referent.list === "A");
}

/** List B. Everything else a blank can point at. Ruling 29. */
export function otherReferents(
  vocabulary: BlankReferentVocabulary = blankReferents,
): readonly BlankReferent[] {
  return vocabulary.referents.filter((referent) => referent.list === "B");
}

/**
 * List B terms with no doctrinal source yet. Reported, never disabled.
 *
 * They still type blanks. The list exists so that the flag is visible rather
 * than buried in a data file, and so that grounding one is a decision someone
 * makes rather than something that quietly happens.
 */
export function ungroundedReferents(
  vocabulary: BlankReferentVocabulary = blankReferents,
): readonly BlankReferent[] {
  return vocabulary.referents.filter((referent) => referent.source === null);
}

/**
 * The term a piece of text names, or null.
 *
 * **Separator-insensitive**, by the ruling of 18 September 2026: the
 * vocabulary term is the concept, not its spelling. `report line`,
 * `Report Line` and `report-line` are the same term, and a sidecar that spells
 * one of them out is not making a data error. Twenty-one of the twenty-seven
 * referents in the first authored sidecar were invisible until this existed.
 *
 * Only separators and case are normalised. Nothing is matched by prefix, by
 * abbreviation or by similarity — an unrecognised term stays unrecognised.
 */
export function referentIdFor(
  value: string,
  vocabulary: BlankReferentVocabulary = blankReferents,
): string | null {
  const wanted = normalizeReferentKey(value);
  return (
    vocabulary.referents.find(
      (referent) => normalizeReferentKey(referent.id) === wanted,
    )?.id ?? null
  );
}

function normalizeReferentKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replaceAll(/[\s_]+/g, "-")
    .replaceAll(/-+/g, "-");
}

/** The referent a term resolves to, whatever its spelling. */
export function referentFor(
  value: string,
  vocabulary: BlankReferentVocabulary = blankReferents,
): BlankReferent | null {
  const id = referentIdFor(value, vocabulary);
  return id === null
    ? null
    : /* v8 ignore next -- referentIdFor returns an id that is in the file */
      (vocabulary.referents.find((referent) => referent.id === id) ?? null);
}

/**
 * True where Ruling 32 refuses a grid: a List A measure that is a line.
 *
 * Deliberately narrow. A List B term that happens to be linear is not
 * refused, because Ruling 32 named control measures and extending it would be
 * an implementer's judgement.
 */
export function isLinearControlMeasure(
  value: string,
  vocabulary: BlankReferentVocabulary = blankReferents,
): boolean {
  const referent = referentFor(value, vocabulary);
  return referent?.list === "A" && referent.geometry === "linear";
}
